/**
 * Training e-sign — /training page.
 *
 * Table layout (client spec): training number, doc type, assigned date,
 * signed date, countersigned by, countersigned at, status, actions.
 * Actions column always shows "View document"; "Sign & complete" sits
 * beside it, disabled until the trainee has scrolled the document to the
 * end (read gate). Signing opens an embedded DocuSeal acknowledgement
 * page (study/site/document metadata + staff name + date), then routes
 * to the study PI for countersignature — a "Countersign" action appears
 * for PI/SI/Coordinator/admin roles on rows pending their signature.
 *
 * Role-based scoping: PI, Sub-I, Coordinator (and the other oversight
 * roles already wired into GET /training/certifications) see every
 * training row for their site — their own included — not just their own.
 * Everyone else sees only their own assignments via GET /training/me.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  ClipboardPlus,
  Clock,
  Eye,
  History,
  Link2,
  Loader2,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Stamp,
  UserCog,
  X,
} from 'lucide-react';
import { pollAndFinalizeTrainingSignature } from '@/lib/trainingSignaturePoll';
import {
  training,
  studies,
  studyStaff,
  type TrainingCertificationRow,
  type TrainingCertificationStatus,
  type EnrichedManualTrainingRow,
} from '@/api/client';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { NewTrainingDialog } from './NewTrainingDialog';
import * as pdfjsLib from 'pdfjs-dist';
// Vite-specific asset URL — pdfjs worker MUST be served as a real URL (see
// ConsentFieldLayoutEditor.tsx, the same setup this mirrors).
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { errorText } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Mirrors handlers/training/listForAdmin.ts's OVERSIGHT_ROLES exactly.
const OVERSIGHT_ROLES = new Set([
  'platform_admin', 'application_admin', 'compliance',
  'site_admin', 'site_study_designer', 'regulatory_specialist',
  'principal_investigator', 'sub_investigator', 'coordinator', 'monitor',
]);

// Training e-sign — manual "assign training" action. Narrower than
// isOversight (which also includes Monitor/regulatory_specialist/etc.,
// who shouldn't be creating assignments) — client: "who will be creating
// that training?... PI or coordinator... by default site admin."
const CAN_ASSIGN_TRAINING_ROLES = new Set([
  'platform_admin', 'application_admin', 'site_admin',
  'principal_investigator', 'coordinator',
]);

// Mirrors handlers/training/manual/list.ts's VISIBLE_ROLES exactly — client:
// "this rejected will be shown to PI, SI, coordinator, and admin roles."
const CAN_SEE_REJECTED_ROLES = new Set([
  'platform_admin', 'application_admin', 'site_admin',
  'principal_investigator', 'sub_investigator', 'coordinator',
]);

type Tab = 'all' | 'rejected';

// Filter options for the merged Training tab — mirrors displayStatus's
// buckets. 'in_progress' is omitted: no code path ever writes that status
// onto a certification (assigned -> completed directly), so it would only
// ever show an empty result.
type StatusFilter = 'all' | 'assigned' | 'staff_signed' | 'completed';

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'assigned', label: 'Assigned — not signed' },
  { value: 'staff_signed', label: 'Staff signed' },
  { value: 'completed', label: 'Completed' },
];

function matchesStatusFilter(row: TrainingCertificationRow, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'staff_signed') return row.status === 'completed' && !row.countersigned_at;
  if (filter === 'completed') return row.status === 'completed' && !!row.countersigned_at;
  return row.status === filter;
}

export function TrainingPage() {
  const { user } = useAuth();
  const trainPerms = useMyPermissions();
  const [tab, setTab] = useState<Tab>('all');
  const [expandSuperseded, setExpandSuperseded] = useState(false);
  const [viewing, setViewing] = useState<TrainingCertificationRow | null>(null);
  const [signDialog, setSignDialog] = useState<{ row: TrainingCertificationRow; mode: 'sign' | 'countersign' } | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [reassigning, setReassigning] = useState<TrainingCertificationRow | null>(null);
  const recheckQc = useQueryClient();
  // "Re-check" on a row stuck at "Awaiting countersignature": re-read the
  // DocuSeal envelope and record whichever signature Caspio is missing.
  // The read is harmless; the write only happens when DocuSeal really has
  // a newer signature than we do.
  const recheckMut = useMutation({
    mutationFn: (row: TrainingCertificationRow) =>
      pollAndFinalizeTrainingSignature(row.certification_id, { attempts: 2, intervalMs: 1000 }),
    onSuccess: (r, row) => {
      recheckQc.invalidateQueries({ queryKey: ['training'] });
      recheckQc.invalidateQueries({ queryKey: ['signature-queue'] });
      if (r.synced && r.countersigned_at) toast.success(`${row.reference_number}: countersignature recorded.`);
      else if (r.synced) toast.success(`${row.reference_number}: signature recorded.`);
      else if (r.countersigned_at) toast.info(`${row.reference_number} is already countersigned.`);
      else toast.info(`${row.reference_number}: DocuSeal has no countersignature yet — open Countersign to sign.`);
    },
    onError: (e) => toast.error(errorText(e, 'Could not re-check the signature status.')),
  });
  const [studyFilter, setStudyFilter] = useState('');
  // Punch-list b9 — group the flat list by study when scanning across
  // studies. Client (2026-09-22): all trainings should be grouped by
  // study by default; the toggle stays so a user can flatten the view.
  const [groupByStudy, setGroupByStudy] = useState(true);
  // Dashboard "My pending training" card links here as
  // ?status=assigned&mine=1. `mine` matters for oversight roles, who
  // otherwise see every staff member's rows rather than their own.
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    const s = searchParams.get('status');
    return STATUS_FILTER_OPTIONS.some((o) => o.value === s) ? (s as StatusFilter) : 'all';
  });
  const [staffFilter, setStaffFilter] = useState(() =>
    searchParams.get('mine') === '1' ? (user?.id ?? '') : '',
  );
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>(25);

  const isOversight = !!user?.role && OVERSIGHT_ROLES.has(user.role);
  const canAssignTraining = trainPerms.holdsOperation(
    'training.create',
    [...CAN_ASSIGN_TRAINING_ROLES],
  );
  const canSeeRejected = !!user?.role && CAN_SEE_REJECTED_ROLES.has(user.role);

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: () => studies.list(),
    enabled: isOversight,
    staleTime: 60_000,
  });
  const studyOptions = studiesQ.data?.rows ?? [];

  const q = useQuery({
    queryKey: isOversight ? ['training', 'oversight', studyFilter] : ['training', 'me'],
    queryFn: () =>
      isOversight
        ? training.list({ limit: 1000, ...(studyFilter ? { study_id: studyFilter } : {}) })
        : training.listMine({ limit: 500 }),
    staleTime: 15_000,
    // A sign/countersign completion updates Caspio via the DocuSeal webhook
    // (confirmed live, 2026-09-03 — it does reach the backend and does
    // apply the event) or via TrainingSignDialog's own poll+finalize — but
    // neither one refreshes THIS list on its own if the dialog that
    // triggered it has already been closed, or if a different tab/device
    // did the signing. Matches the same 30s cadence already used by
    // SignatureQueuePage/ApprovalNeededPage/DashboardPage for the same
    // "another actor changed something server-side" reason.
    refetchInterval: 30_000,
  });

  const rows = q.data?.rows ?? [];

  const rejectedQ = useQuery({
    queryKey: ['training', 'manual', 'rejected', studyFilter],
    queryFn: () => training.manual.list({ status: 'rejected', ...(studyFilter ? { study_id: studyFilter } : {}) }),
    enabled: canSeeRejected,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const rejectedRows = rejectedQ.data?.rows ?? [];

  // Client: "can we have it in a single tab?" — one combined list regardless
  // of status (Assigned / Staff signed / Completed), distinguished only by
  // the status badge (displayStatus below). Superseded stays in its own
  // collapsible section rather than inline in the main list.
  const { visible, superseded } = useMemo(() => {
    const visible: TrainingCertificationRow[] = [];
    const superseded: TrainingCertificationRow[] = [];
    for (const row of rows) {
      if (row.status === 'superseded') superseded.push(row);
      else visible.push(row);
    }
    return { visible, superseded };
  }, [rows]);

  const staffOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of visible) {
      if (row.trainee_name) seen.set(row.user_id, row.trainee_name);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [visible]);

  const docTypeOptions = useMemo(
    () => [...new Set(visible.map((row) => row.document_type_name).filter(Boolean))].sort(),
    [visible],
  );

  const filtered = tab === 'all'
    ? visible.filter(
      (row) =>
        matchesStatusFilter(row, statusFilter) &&
        (!staffFilter || row.user_id === staffFilter) &&
        (!docTypeFilter || row.document_type_name === docTypeFilter),
    )
    : [];

  const pageCount = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const active = pageSize === 'all' ? filtered : filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Punch-list b9 — grouped view shows every matching row (not just the
  // current page) so a study's rows never split across pages.
  const groupedByStudy = useMemo(() => {
    if (!groupByStudy) return null;
    const groups = new Map<string, TrainingCertificationRow[]>();
    for (const row of filtered) {
      // Name primary, protocol number as the secondary identifier (client
      // rule, 2026-10-09). Grouping on the protocol number alone is why this
      // page read "STD-2026-0020 (6)" instead of naming the study.
      const key = row.study_title
        ? `${row.study_title}${row.study_protocol_number ? ` · ${row.study_protocol_number}` : ''}`
        : row.study_protocol_number || 'Unknown study';
      const bucket = groups.get(key);
      if (bucket) bucket.push(row);
      else groups.set(key, [row]);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [groupByStudy, filtered]);

  // Any filter/tab change invalidates the current page — jump back to 1
  // rather than showing an empty page 4 of a now-2-page result.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [tab, statusFilter, staffFilter, docTypeFilter, studyFilter, pageSize]);

  // Grouping and the single-study filter are mutually exclusive — the
  // toggle is hidden once a study is picked, so keep the state in sync.
  useEffect(() => { if (studyFilter) setGroupByStudy(false); }, [studyFilter]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Training"
        subtitle={
          isOversight
            ? 'Every training certification for staff at this site — review status, view documents, and countersign completed sign-offs.'
            : 'Certifications assigned to you for study documents that require training. Review each document then sign off.'
        }
        actions={
          canAssignTraining && (
            <button
              type="button"
              onClick={() => setAssigning(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
            >
              <ClipboardPlus className="h-3.5 w-3.5" /> New training
            </button>
          )
        }
      />

      <div className="border-b border-surface-border">
        <nav className="-mb-px flex gap-1" aria-label="Training tabs">
          <TabButton
            active={tab === 'all'}
            onClick={() => setTab('all')}
            icon={<ClipboardPlus className="h-4 w-4" />}
            label={`Training${visible.length ? ` (${visible.length})` : ''}`}
          />
          {canSeeRejected && (
            <TabButton
              active={tab === 'rejected'}
              onClick={() => setTab('rejected')}
              icon={<X className="h-4 w-4" />}
              label={`Rejected${rejectedRows.length ? ` (${rejectedRows.length})` : ''}`}
            />
          )}
        </nav>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {tab === 'all' && (
          <select
            className="input w-48 shrink-0 text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
        {tab === 'all' && isOversight && (
          <select
            className="input w-48 shrink-0 text-xs"
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
          >
            <option value="">All staff</option>
            {staffOptions.map(([userId, name]) => (
              <option key={userId} value={userId}>
                {name}
              </option>
            ))}
          </select>
        )}
        {tab === 'all' && (
          <select
            className="input w-48 shrink-0 text-xs"
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
          >
            <option value="">All doc types</option>
            {docTypeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
        {isOversight && (
          <select
            className="input w-48 shrink-0 text-xs"
            value={studyFilter}
            onChange={(e) => setStudyFilter(e.target.value)}
            disabled={studiesQ.isLoading}
          >
            <option value="">All studies</option>
            {studyOptions.map((s) => (
              <option key={s.study_id} value={s.study_id}>
                {studyLabel(s)}
              </option>
            ))}
          </select>
        )}
        {tab === 'all' && isOversight && !studyFilter && (
          <label className="flex shrink-0 items-center gap-1.5 text-xs text-ink-secondary">
            <input
              type="checkbox"
              checked={groupByStudy}
              onChange={(e) => setGroupByStudy(e.target.checked)}
            />
            Group by study
          </label>
        )}
      </div>

      {tab === 'rejected' ? (
        rejectedQ.isLoading ? (
          <div className="card flex items-center justify-center gap-2 p-10 text-sm text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin text-accent-600" />
            Loading rejected trainings…
          </div>
        ) : rejectedRows.length === 0 ? (
          <div className="card p-10 text-center text-sm text-ink-muted">No rejected trainings.</div>
        ) : (
          <RejectedTrainingsTable rows={rejectedRows} />
        )
      ) : q.isLoading ? (
        <div className="card flex items-center justify-center gap-2 p-10 text-sm text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin text-accent-600" />
          Loading training records…
        </div>
      ) : (groupedByStudy ? filtered.length === 0 : active.length === 0) ? (
        <div className="card p-10 text-center text-sm text-ink-muted">
          <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
          {statusFilter !== 'all' || staffFilter || docTypeFilter
            ? 'No training matches these filters.'
            : isOversight
              ? 'No training records for this site.'
              : "You're up to date — no training assigned."}
        </div>
      ) : groupedByStudy ? (
        <div className="space-y-4">
          {groupedByStudy.map(([studyLabel, studyRows]) => (
            <div key={studyLabel}>
              <h3 className="mb-1.5 text-xs font-semibold text-ink-primary">
                {studyLabel} <span className="font-normal text-ink-muted">({studyRows.length})</span>
              </h3>
              <TrainingTable
                rows={studyRows}
                currentUserId={user?.id ?? ''}
                isOversight={isOversight}
                onView={setViewing}
                onSign={(row) => setSignDialog({ row, mode: 'sign' })}
                onCountersign={(row) => setSignDialog({ row, mode: 'countersign' })}
                onReassignCountersign={setReassigning}
                onRecheck={(row) => recheckMut.mutate(row)}
                recheckingId={recheckMut.isPending ? recheckMut.variables?.certification_id ?? null : null}
              />
            </div>
          ))}
        </div>
      ) : (
        <TrainingTable
          rows={active}
          currentUserId={user?.id ?? ''}
          isOversight={isOversight}
          onView={setViewing}
          onSign={(row) => setSignDialog({ row, mode: 'sign' })}
          onCountersign={(row) => setSignDialog({ row, mode: 'countersign' })}
          onReassignCountersign={setReassigning}
          onRecheck={(row) => recheckMut.mutate(row)}
          recheckingId={recheckMut.isPending ? recheckMut.variables?.certification_id ?? null : null}
        />
      )}

      {tab === 'all' && filtered.length > 0 && !groupedByStudy && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-ink-muted">
          <span>
            {pageSize === 'all'
              ? `Showing all ${filtered.length}`
              : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length}`}
          </span>
          <div className="flex items-center gap-2">
            <select
              className="input w-28 shrink-0 text-xs"
              value={String(pageSize)}
              onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            >
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
              <option value="all">All</option>
            </select>
            {pageSize !== 'all' && (
              <>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span>
                  Page {safePage} of {pageCount}
                </span>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {superseded.length > 0 && tab === 'all' && (
        <div className="rounded border border-surface-border bg-surface-muted/30 p-3">
          <button
            type="button"
            onClick={() => setExpandSuperseded((v) => !v)}
            className="flex w-full items-center justify-between text-left text-xs font-medium text-ink-secondary hover:text-ink-primary"
          >
            <span className="flex items-center gap-1.5">
              <History className="h-3 w-3" />
              Superseded certifications ({superseded.length})
            </span>
            <span>{expandSuperseded ? '−' : '+'}</span>
          </button>
          {expandSuperseded && (
            <div className="mt-2">
              <TrainingTable
                rows={superseded}
                currentUserId={user?.id ?? ''}
                isOversight={isOversight}
                onView={setViewing}
                onSign={() => { }}
                onCountersign={() => { }}
                onReassignCountersign={() => { }}
                onRecheck={() => { }}
                superseded
              />
            </div>
          )}
        </div>
      )}

      {viewing && (
        <ViewDocumentModal
          row={viewing}
          isMine={viewing.user_id === user?.id}
          onClose={() => setViewing(null)}
        />
      )}

      {signDialog && (
        <TrainingSignDialog
          row={signDialog.row}
          mode={signDialog.mode}
          onClose={() => setSignDialog(null)}
        />
      )}

      <NewTrainingDialog
        open={assigning}
        onClose={() => setAssigning(false)}
        onCreated={() => setAssigning(false)}
      />

      {reassigning && (
        <ReassignCountersignDialog row={reassigning} onClose={() => setReassigning(null)} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3 text-sm font-semibold ${active
        ? 'border-accent-600 text-accent-700'
        : 'border-transparent text-ink-secondary hover:text-ink-primary'
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

const STATUS_LABELS: Record<TrainingCertificationStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  completed: 'Completed',
  superseded: 'Superseded',
};

const STATUS_BADGE: Record<TrainingCertificationStatus, string> = {
  assigned: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-sky-50 text-sky-700 border-sky-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  superseded: 'bg-slate-100 text-slate-600 border-slate-200',
};

/**
 * `status === 'completed'` in the data model means "the trainee has
 * signed" — it says nothing about the countersignature. Showing that as
 * a flat green "Completed" badge reads as fully done when it might only
 * be half done. Split the display (not the underlying status, which
 * still correctly drives the Outstanding/Completed tab split) into three
 * distinct, unambiguous states.
 */
function displayStatus(row: TrainingCertificationRow): { label: string; badgeClass: string } {
  if (row.status === 'completed') {
    return row.countersigned_at
      ? { label: 'Completed', badgeClass: STATUS_BADGE.completed }
      : { label: 'Staff signed', badgeClass: 'bg-sky-50 text-sky-700 border-sky-200' };
  }
  if (row.status === 'assigned') {
    return { label: 'Assigned — not signed', badgeClass: STATUS_BADGE.assigned };
  }
  return { label: STATUS_LABELS[row.status], badgeClass: STATUS_BADGE[row.status] };
}

function TrainingTable({
  rows,
  currentUserId,
  isOversight,
  onView,
  onSign,
  onCountersign,
  onReassignCountersign,
  onRecheck,
  recheckingId,
  superseded,
}: {
  rows: TrainingCertificationRow[];
  currentUserId: string;
  isOversight: boolean;
  onView: (row: TrainingCertificationRow) => void;
  onSign: (row: TrainingCertificationRow) => void;
  onCountersign: (row: TrainingCertificationRow) => void;
  onReassignCountersign: (row: TrainingCertificationRow) => void;
  /** Re-read the DocuSeal envelope for a row stuck on "Awaiting countersignature". */
  onRecheck: (row: TrainingCertificationRow) => void;
  recheckingId?: string | null;
  superseded?: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded border border-surface-border bg-surface-card">
      <table className="w-full min-w-[900px] text-left text-xs">
        <thead className="border-b border-surface-border bg-surface-muted/30 text-[10px] uppercase tracking-wide text-ink-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Training #</th>
            {isOversight && <th className="px-3 py-2 font-medium">Study</th>}
            {isOversight && <th className="px-3 py-2 font-medium">Staff</th>}
            <th className="px-3 py-2 font-medium">Doc type</th>
            <th className="px-3 py-2 font-medium">Document name</th>
            <th className="px-3 py-2 font-medium">Assigned</th>
            <th className="px-3 py-2 font-medium">Signed</th>
            <th className="px-3 py-2 font-medium">Countersigned by</th>
            <th className="px-3 py-2 font-medium">Countersigned at</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {rows.map((row) => {
            const isMine = row.user_id === currentUserId;
            const isOutstanding = row.status === 'assigned' || row.status === 'in_progress';
            const pendingCountersign = row.status === 'completed' && !row.countersigned_at;
            // Countersignature authority is per-certification identity, not a
            // role check: the delegate once the PI has reassigned it, else
            // this specific study's actual PI (row.study_pi_user_id) — never
            // "anyone holding the PI/Sub-I/Coordinator role somewhere." A
            // training assigned directly to the study's own PI still needs
            // this same PI to countersign it as a second, separate signature,
            // so this deliberately does NOT exclude isMine.
            // Caspio returns an unset field as '' rather than null, so `??`
            // alone never falls through to study_pi_user_id here.
            const resolvedCountersignerId = row.countersign_delegate_user_id || row.study_pi_user_id || null;
            const canActOnCountersign =
              pendingCountersign && !!resolvedCountersignerId && resolvedCountersignerId === currentUserId;
            // Only this study's actual PI can hand countersignature off to a
            // Sub-Investigator — matches reassignCountersign.ts's own check
            // (study.pi_user_id === caller), not a role check, so a PI on a
            // different study never sees this for someone else's training.
            // No admin bypass — client, 2026-09-03: "admins cannot sign,
            // only PI — if PI gives to SI, then SI only."
            const canReassign =
              pendingCountersign && !!row.study_pi_user_id && row.study_pi_user_id === currentUserId;
            return (
              <tr key={row.certification_id} className={superseded ? 'opacity-70' : ''}>
                <td className="px-3 py-2 align-top font-mono text-[11px] text-ink-secondary">{row.reference_number}</td>
                {isOversight && (
                  <td className="px-3 py-2 align-top text-ink-secondary">
                    {row.study_title ? (
                      <>
                        <div className="text-ink-primary">{row.study_title}</div>
                        {row.study_protocol_number && (
                          <div className="font-mono text-[11px]">{row.study_protocol_number}</div>
                        )}
                      </>
                    ) : (
                      <span className="font-mono text-[11px]">{row.study_protocol_number || '—'}</span>
                    )}
                  </td>
                )}
                {isOversight && (
                  <td className="px-3 py-2 align-top text-ink-primary">
                    {row.trainee_name || '—'}
                    {isMine && <span className="ml-1 text-[10px] text-ink-muted">(you)</span>}
                  </td>
                )}
                <td className="px-3 py-2 align-top text-ink-primary">{row.document_type_name}</td>
                <td className="max-w-[280px] truncate px-3 py-2 align-top text-ink-muted" title={row.document_title || undefined}>{row.document_title || '—'}</td>
                <td className="px-3 py-2 align-top text-ink-muted">{fmtDate(row.assigned_at)}</td>
                <td className="px-3 py-2 align-top text-ink-muted">{row.completed_at ? fmtDateTime(row.completed_at) : '—'}</td>
                <td className="px-3 py-2 align-top text-ink-muted">{row.countersigner_name ?? '—'}</td>
                <td className="px-3 py-2 align-top text-ink-muted">{row.countersigned_at ? fmtDateTime(row.countersigned_at) : '—'}</td>
                <td className="px-3 py-2 align-top">
                  {(() => {
                    const { label, badgeClass } = displayStatus(row);
                    return (
                      <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${badgeClass}`}>
                        {label}
                      </span>
                    );
                  })()}
                  {pendingCountersign && (
                    <div className="mt-0.5 text-[10px] text-amber-700">
                      <Clock className="mr-0.5 inline h-2.5 w-2.5" />{' '}
                      {row.countersign_delegate_user_id ? 'Awaiting SI countersignature' : 'Awaiting PI countersignature'}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onView(row)}
                      className="inline-flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted/50"
                    >
                      <Eye className="h-3 w-3" /> View
                    </button>
                    {isOutstanding && isMine && (
                      <button
                        type="button"
                        disabled={!row.read_confirmed_at}
                        onClick={() => onSign(row)}
                        title={!row.read_confirmed_at ? 'Scroll to the end of the document to unlock signing' : undefined}
                        className="inline-flex items-center gap-1 rounded bg-accent-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <PenLine className="h-3 w-3" /> Sign &amp; complete
                      </button>
                    )}
                    {canActOnCountersign && (
                      <button
                        type="button"
                        onClick={() => onCountersign(row)}
                        className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-700"
                      >
                        <Stamp className="h-3 w-3" /> Countersign
                      </button>
                    )}
                    {canReassign && (
                      <button
                        type="button"
                        onClick={() => onReassignCountersign(row)}
                        className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-50"
                      >
                        <UserCog className="h-3 w-3" /> Reassign to SI
                      </button>
                    )}
                    {pendingCountersign && (canActOnCountersign || isOversight) && (
                      <button
                        type="button"
                        onClick={() => onRecheck(row)}
                        disabled={recheckingId === row.certification_id}
                        title="Already countersigned in DocuSeal but still showing as awaiting? Re-read the signature status."
                        className="inline-flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3 w-3 ${recheckingId === row.certification_id ? 'animate-spin' : ''}`} /> Re-check
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Standalone/external training — "Rejected" tab. Client: "the rejected
 * training should be maintained... we can add one more tab next to
 * completed, Rejected there we can show details training no, name,
 * document/external link, created by, created at, rejected reason,
 * rejected by, rejected at." A rejected manual training never generates a
 * certification row, so it has nowhere else to surface once the PI's
 * approval-queue task for it resolves — this table reads straight from
 * `ctms_manual_trainings` instead of `ctms_training_certifications`.
 */
function RejectedTrainingsTable({ rows }: { rows: EnrichedManualTrainingRow[] }) {
  return (
    <div className="overflow-x-auto rounded border border-surface-border bg-surface-card">
      <table className="w-full min-w-[900px] text-left text-xs">
        <thead className="border-b border-surface-border bg-surface-muted/30 text-[10px] uppercase tracking-wide text-ink-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Training #</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Document / link</th>
            <th className="px-3 py-2 font-medium">Created by</th>
            <th className="px-3 py-2 font-medium">Created at</th>
            <th className="px-3 py-2 font-medium">Rejected reason</th>
            <th className="px-3 py-2 font-medium">Rejected by</th>
            <th className="px-3 py-2 font-medium">Rejected at</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {rows.map((row) => (
            <tr key={row.manual_training_id}>
              <td className="px-3 py-2 align-top font-mono text-[11px] text-ink-secondary">{row.reference_number}</td>
              <td className="px-3 py-2 align-top text-ink-primary">{row.training_name}</td>
              <td className="px-3 py-2 align-top text-ink-muted">
                {row.training_kind === 'external' ? (
                  row.external_link ? (
                    <a
                      href={row.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-accent-700 hover:underline"
                    >
                      <Link2 className="h-3 w-3" /> External link
                    </a>
                  ) : (
                    '—'
                  )
                ) : (
                  row.source_file_name || '—'
                )}
              </td>
              <td className="px-3 py-2 align-top text-ink-muted">{row.created_by_name || '—'}</td>
              <td className="px-3 py-2 align-top text-ink-muted">{fmtDate(row.created_at)}</td>
              <td className="max-w-[280px] px-3 py-2 align-top text-ink-muted">{row.rejection_reason || '—'}</td>
              <td className="px-3 py-2 align-top text-ink-muted">{row.rejected_by_name || '—'}</td>
              <td className="px-3 py-2 align-top text-ink-muted">{row.rejected_at ? fmtDate(row.rejected_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Training e-sign — PI hands off countersignature to a Sub-Investigator
 * (client: "PI may be delegating it to Sub-I. That's the reason it will
 * go, otherwise it is a default"). Just a picker + submit — the chosen
 * Sub-I gets a fresh DocuSeal email invite (sendEmail: true server-side)
 * and signs later from their own "Countersign" button; the PI reassigning
 * doesn't need to see any embed page themselves.
 */
function ReassignCountersignDialog({
  row,
  onClose,
}: {
  row: TrainingCertificationRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [delegateUserId, setDelegateUserId] = useState('');

  const staffQ = useQuery({
    queryKey: ['study-staff', row.study_id],
    queryFn: () => studyStaff.list(row.study_id),
    staleTime: 30_000,
  });
  // Exclude whoever countersignature is ALREADY delegated to — reassigning
  // to the same person again is a no-op the dialog shouldn't offer.
  const subInvestigators = (staffQ.data?.assignments ?? []).filter(
    (a) => a.role === 'sub_investigator' && a.user_id !== row.countersign_delegate_user_id,
  );

  const reassignMut = useMutation({
    mutationFn: () => {
      if (!delegateUserId) throw new Error('Pick a Sub-Investigator.');
      return training.reassignCountersign(row.certification_id, delegateUserId);
    },
    onSuccess: () => {
      toast.success('Countersignature reassigned — the Sub-Investigator has been emailed a signing link.');
      qc.invalidateQueries({ queryKey: ['training'] });
      onClose();
    },
    onError: (e) => toast.error(errorText(e, 'Failed to reassign countersignature')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4">
      <div className="card w-full max-w-sm space-y-4 p-6">
        <div className="flex items-start justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink-primary">
            <UserCog className="h-4 w-4" /> Reassign to Sub-Investigator
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={reassignMut.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-ink-muted">
          {row.reference_number} · {row.trainee_name}
        </p>

        {staffQ.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Sub-Investigators…
          </div>
        ) : subInvestigators.length === 0 ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {row.countersign_delegate_user_id
              ? 'No other Sub-Investigator is available to reassign to on this study.'
              : 'No Sub-Investigator is currently delegated on this study.'}
          </div>
        ) : (
          <select
            className="input w-full"
            value={delegateUserId}
            disabled={reassignMut.isPending}
            onChange={(e) => setDelegateUserId(e.target.value)}
          >
            <option value="" disabled>
              Pick a Sub-Investigator…
            </option>
            {subInvestigators.map((a) => (
              <option key={a.user_id} value={a.user_id}>
                {a.full_name} ({a.email})
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={reassignMut.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!delegateUserId || reassignMut.isPending}
            onClick={() => reassignMut.mutate()}
          >
            {reassignMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
            Reassign
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Read-only "View document" modal — scroll-to-end read gate (client:
 * "after they scroll down we set a flag to enable sign complete button").
 * Stamps read_confirmed_at server-side once, harmless to re-confirm.
 */
function ViewDocumentModal({
  row,
  isMine,
  onClose,
}: {
  row: TrainingCertificationRow;
  isMine: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [readConfirmed, setReadConfirmed] = useState(!!row.read_confirmed_at);
  const [confirming, setConfirming] = useState(false);
  // Oversight roles can open someone else's training to review it (per the
  // backend's own isMine-or-oversight check), but only the actual assignee
  // ever sees or can advance the read-gate — an oversight viewer just gets
  // a plain read-only preview.
  const needsGate = isMine && (row.status === 'assigned' || row.status === 'in_progress');

  const confirmReadMut = useMutation({
    mutationFn: () => training.confirmRead(row.certification_id),
    onSuccess: () => {
      setReadConfirmed(true);
      qc.invalidateQueries({ queryKey: ['training'] });
    },
    onError: (e) => {
      toast.error(errorText(e, 'Failed to record read confirmation'));
      setConfirming(false);
    },
  });

  const handleReachedEnd = () => {
    if (!needsGate || readConfirmed || confirming) return;
    setConfirming(true);
    confirmReadMut.mutate();
  };

  const handleMarkReviewed = () => {
    if (!needsGate || readConfirmed || confirming) return;
    setConfirming(true);
    confirmReadMut.mutate();
  };

  const isExternal = row.manual_training_kind === 'external';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">{row.document_title || row.document_type_name}</h3>
            <p className="text-[11px] text-ink-muted">{row.reference_number}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-surface-muted/50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {isExternal ? (
            <ExternalTrainingPanel
              row={row}
              needsGate={needsGate}
              readConfirmed={readConfirmed}
              confirming={confirming}
              onMarkReviewed={handleMarkReviewed}
            />
          ) : (
            <TrainingDocumentViewer certificationId={row.certification_id} onReachedEnd={handleReachedEnd} />
          )}

          {!isExternal && needsGate && (
            <div
              className={`flex items-center gap-2 rounded border p-3 text-xs ${readConfirmed
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-surface-border bg-surface-muted/30 text-ink-muted'
                }`}
            >
              {readConfirmed ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  Read confirmed — you can now sign from the training table.
                </>
              ) : confirming ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  Recording read confirmation…
                </>
              ) : (
                <>
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  Scroll to the end of the document above to unlock signing.
                </>
              )}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function guessCertMime(_name: string): string {
  return 'application/pdf';
}

async function uploadCertWithProgress(url: string, file: File, mime: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

/**
 * Standalone/external training — replaces the PDF viewer + read-gate for
 * an External (LMS-link) training, which has no source document to embed.
 * The trainee opens the link themselves, then clicks "Mark as reviewed"
 * to unlock signing — same read-gate contract (`confirmRead`) as the
 * Internal/PDF path, just triggered by a button instead of scroll
 * position. Also offers an optional completion-certificate attach,
 * per client feedback (14 Aug demo-review call).
 */
function ExternalTrainingPanel({
  row,
  needsGate,
  readConfirmed,
  confirming,
  onMarkReviewed,
}: {
  row: TrainingCertificationRow;
  needsGate: boolean;
  readConfirmed: boolean;
  confirming: boolean;
  onMarkReviewed: () => void;
}) {
  const [certFile, setCertFile] = useState<File | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(!!row.completion_certificate_file_name);

  const attachCert = async (file: File) => {
    setAttaching(true);
    try {
      const mime = file.type || guessCertMime(file.name);
      const presigned = await training.presignCompletionCertificate(row.certification_id, {
        file_name: file.name,
        mime_type: mime,
      });
      await uploadCertWithProgress(presigned.upload_url, file, mime);
      await training.attachCompletionCertificate(row.certification_id, {
        s3_bucket: presigned.s3_bucket,
        s3_key: presigned.s3_key,
        file_name: file.name,
        file_size: file.size,
      });
      setAttached(true);
      toast.success('Completion certificate attached.');
    } catch (e) {
      toast.error(errorText(e, 'Failed to attach completion certificate'));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded border border-surface-border bg-surface-muted/30 p-4 text-center">
        <Link2 className="mx-auto mb-2 h-6 w-6 text-accent-600" />
        <p className="text-xs text-ink-muted">This is an external training hosted on a third-party site.</p>
        {row.manual_training_external_link && (
          <a
            href={row.manual_training_external_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
          >
            Open training <Link2 className="h-3 w-3" />
          </a>
        )}
      </div>

      {needsGate && (
        <div
          className={`flex items-center justify-between gap-2 rounded border p-3 text-xs ${
            readConfirmed
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-surface-border bg-surface-muted/30 text-ink-muted'
          }`}
        >
          {readConfirmed ? (
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              Reviewed — you can now sign from the training table.
            </span>
          ) : confirming ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              Recording…
            </span>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                Complete the training above, then confirm here to unlock signing.
              </span>
              <button
                type="button"
                onClick={onMarkReviewed}
                className="shrink-0 rounded bg-accent-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-accent-700"
              >
                Mark as reviewed
              </button>
            </>
          )}
        </div>
      )}

      <div className="rounded border border-surface-border p-3">
        <label className="label text-xs">Completion certificate (optional)</label>
        {attached ? (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Attached
          </p>
        ) : (
          <>
            <input
              type="file"
              className="mt-1 block w-full text-xs"
              disabled={attaching}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setCertFile(f);
                  void attachCert(f);
                }
                e.target.value = '';
              }}
            />
            {attaching && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-muted">
                <Loader2 className="h-3 w-3 animate-spin" /> Uploading {certFile?.name}…
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * DocuSeal acknowledgement + signature page, embedded inline. Shared by
 * both the trainee's "Sign & complete" action and the PI/delegate's
 * "Countersign" action — same envelope, different submitter slot.
 *
 * Check-once-on-close (client, 2026-09-03: "call the api directly... only
 * call api when actual signed happened, if user opens and closes without
 * sign then dont call api"): no periodic poll while the dialog is open —
 * DocuSeal's own iframe already shows its native completion screen once
 * signed, so there's nothing for us to render live. Instead, closing the
 * dialog triggers exactly ONE syncStatus check; only if that reports a
 * real new completion do we call finalizeSync (updates Caspio + the
 * training list) and show our own "captured" screen before letting the
 * user close for real. A close with nothing new skips finalize entirely —
 * no wasted write, matching the ask precisely. The training LIST page
 * also polls independently now (see the `refetchInterval` on the main `q`
 * query above), so a webhook or a different tab/device completing this
 * cert still shows up there without a reload even if this dialog is never
 * reopened.
 */
function TrainingSignDialog({
  row,
  mode,
  onClose,
}: {
  row: TrainingCertificationRow;
  mode: 'sign' | 'countersign';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStarting(true);
    setStartError(null);
    (async () => {
      try {
        const r = mode === 'sign'
          ? await training.startSigning(row.certification_id)
          : await training.startCountersigning(row.certification_id);
        if (!cancelled) setEmbedUrl(r.embed_url);
      } catch (e) {
        if (!cancelled) setStartError(errorText(e, 'Failed to start signing'));
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.certification_id, mode]);

  const checkAndFinalizeMut = useMutation({
    // DocuSeal marks the submitter complete a beat after its iframe shows
    // "done", so a single check at close can miss a signature made seconds
    // ago. Poll briefly; finalize (the only write) runs only on a real
    // new completion.
    mutationFn: async () => {
      const r = await pollAndFinalizeTrainingSignature(row.certification_id);
      return r.synced ? r : null;
    },
    onSuccess: (r) => {
      if (!r) { onClose(); return; }
      const finishedThisStep = mode === 'sign' ? !!r.completed_at : !!r.countersigned_at;
      qc.invalidateQueries({ queryKey: ['training'] });
      // Caspio's own write doesn't always show up on an immediate
      // subsequent read (seen elsewhere this session too) — the first
      // invalidate can race ahead of it and quietly refetch stale data.
      // A second invalidate a couple seconds later is a no-op when the
      // first one already had fresh data, and fixes it when it didn't.
      setTimeout(() => { qc.invalidateQueries({ queryKey: ['training'] }); }, 2500);
      if (finishedThisStep) {
        setDone(true);
        toast.success(mode === 'sign' ? 'Training signed — routed to your PI for countersignature.' : 'Countersignature recorded.');
      } else {
        onClose();
      }
    },
    onError: () => onClose(), // never trap the user behind a failed check
  });

  function handleCloseClick() {
    if (done || checkAndFinalizeMut.isPending) { onClose(); return; }
    // Nothing was ever presented to sign (still loading, or start failed) —
    // no completion is possible, so skip the check entirely.
    if (!embedUrl) { onClose(); return; }
    checkAndFinalizeMut.mutate();
  }

  const title = mode === 'sign' ? 'Sign & complete training' : 'Countersign training';

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink-primary/60 p-4 !m-0">
      <div className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-surface-border px-5 py-3">
          <div>
            <div className="text-sm font-semibold text-ink-primary">{title}</div>
            <div className="mt-0.5 text-[11px] text-ink-muted">
              {row.reference_number} · {row.document_type_name}
              {mode === 'countersign' && row.trainee_name ? ` · ${row.trainee_name}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseClick}
            disabled={checkAndFinalizeMut.isPending}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/40 hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            title={checkAndFinalizeMut.isPending ? 'Checking signing status…' : 'Close'}
          >
            {checkAndFinalizeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </button>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {starting && (
            <div className="flex flex-1 items-center justify-center text-xs text-ink-muted">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing signing session…
            </div>
          )}

          {startError && (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="max-w-sm rounded border border-danger-200 bg-danger-50 p-4 text-center text-xs text-danger-700">
                {startError}
              </div>
            </div>
          )}

          {!starting && !startError && !done && embedUrl && (
            <iframe
              title="Training signature"
              src={embedUrl}
              className="w-full flex-1 border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          )}

          {done && (
            <div className="flex flex-1 flex-col items-center justify-center space-y-3 p-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <div className="text-base font-semibold text-ink-primary">
                {mode === 'sign' ? 'Signature captured' : 'Countersignature captured'}
              </div>
              <p className="max-w-md text-xs text-ink-muted">
                {mode === 'sign'
                  ? 'Your training sign-off has been recorded and routed to your PI for countersignature.'
                  : 'The training certification is now fully closed out and will be filed to the site binder.'}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-accent-600 px-4 py-2 text-xs font-medium text-white hover:bg-accent-700"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Training module Phase 4 — embedded, read-only PDF viewer with a real
 * scroll-to-end gate. Renders every page as a canvas via pdfjs-dist
 * (same library + worker setup as ConsentFieldLayoutEditor.tsx) inside
 * a scrollable container; an IntersectionObserver on a sentinel below
 * the last page fires `onReachedEnd` once the trainee actually scrolls
 * that far — not just on file-select or a bare checkbox.
 */
function TrainingDocumentViewer({
  certificationId,
  onReachedEnd,
}: {
  certificationId: string;
  onReachedEnd: () => void;
}) {
  const urlQ = useQuery({
    queryKey: ['training', 'document-url', certificationId],
    queryFn: () => training.getDocumentUrl(certificationId),
    staleTime: 10 * 60 * 1000,
  });

  const pdfDocQ = useQuery({
    queryKey: ['training', 'pdf-doc', certificationId, urlQ.data?.url],
    queryFn: async () => {
      const task = pdfjsLib.getDocument({ url: urlQ.data!.url, disableAutoFetch: false, disableStream: false });
      return task.promise;
    },
    enabled: !!urlQ.data?.url,
    retry: false,
    staleTime: Infinity,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !pdfDocQ.data) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onReachedEnd();
        }
      },
      { root, threshold: 0.99 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocQ.data]);

  if (urlQ.isLoading || pdfDocQ.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center rounded border border-surface-border bg-surface-muted/20 text-xs text-ink-muted">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading document…
      </div>
    );
  }
  if (urlQ.isError || pdfDocQ.isError) {
    return (
      <div className="rounded border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
        Could not load the training document. Refresh and try again.
      </div>
    );
  }

  const pageCount = pdfDocQ.data?.numPages ?? 0;

  return (
    <div
      ref={scrollRef}
      className="max-h-[45vh] space-y-2 overflow-y-auto rounded border border-surface-border bg-surface-muted/10 p-2"
    >
      {Array.from({ length: pageCount }, (_, i) => (
        <TrainingPdfPage key={i} pdfDoc={pdfDocQ.data ?? null} pageIndex={i} />
      ))}
      <div ref={sentinelRef} className="h-1" aria-hidden />
    </div>
  );
}

function TrainingPdfPage({
  pdfDoc,
  pageIndex,
}: {
  pdfDoc: import('pdfjs-dist').PDFDocumentProxy | null;
  pageIndex: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    const pageNumber = pageIndex + 1;
    if (pageNumber > pdfDoc.numPages) return;
    const canvas = canvasRef.current;
    let cancelled = false;
    let renderTask: import('pdfjs-dist').RenderTask | null = null;
    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;
        const dpr = window.devicePixelRatio || 1;
        const scale = 1.1 * dpr;
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        renderTask = page.render({ canvas, canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (err) {
        const msg = (err as Error)?.message ?? '';
        if (!/cancel|abort/i.test(msg)) {
          console.warn(`[TrainingDocumentViewer] page ${pageIndex + 1} render failed:`, err);
        }
      }
    })();
    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch { /* noop */ }
    };
  }, [pdfDoc, pageIndex]);

  return (
    <div className="flex justify-center">
      <canvas ref={canvasRef} className="rounded border border-surface-border shadow-sm" />
    </div>
  );
}
